/**
 * Utility class for formatting values
 */
export class FormattedUtils {
    
    /**
     * Formats price with thousand separators and 2 decimal places
     * @param {number} price - The price to format
     * @param {string} currencySymbol - The currency symbol to append
     * @returns {string} Formatted price string
     */
    static formatPrice(price, currencySymbol = '') {
        const formattedPrice = price?.toLocaleString('pl-PL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true
        }) || '0,00';
        
        return `${formattedPrice} ${currencySymbol}`.trim();
    }

    /**
     * Formats bytes into human-readable format with appropriate units
     * @param {number} bytes - Number of bytes to format
     * @param {number} decimals - Number of decimal places (default: 1)
     * @returns {string} Formatted bytes string with unit
     * 
     * @example
     * FormattedUtils.formatBytes(1024) // "1,0 KB"
     * FormattedUtils.formatBytes(1234567) // "1,2 MB"
     * FormattedUtils.formatBytes(500) // "500 B"
     * FormattedUtils.formatBytes(0) // "0 B"
     */
    static formatBytes(bytes, decimals = 1) {
        if (bytes === 0) return '0 B';
        
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        
        // For small files (less than 1 KB) show without decimal places
        if (i === 0) {
            return `${bytes} B`;
        }
        
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
        
        // Remove unnecessary trailing zeros
        const cleanSize = size % 1 === 0 ? size.toFixed(0) : size.toFixed(decimals);
        
        // Use Polish locale for comma as decimal separator
        const formattedSize = cleanSize.replace('.', ',');
        
        return `${formattedSize} ${sizes[i]}`;
    }

    /**
     * Formats bytes into compact format without spaces
     * @param {number} bytes - Number of bytes to format
     * @param {number} decimals - Number of decimal places (default: 1)
     * @returns {string} Compact formatted bytes string
     * 
     * @example
     * FormattedUtils.formatBytesCompact(1024) // "1,0KB"
     * FormattedUtils.formatBytesCompact(1234567) // "1,2MB"
     */
    static formatBytesCompact(bytes, decimals = 1) {
        return this.formatBytes(bytes, decimals).replace(' ', '');
    }

    /**
     * Formats bytes with precise decimal places (2 decimal places)
     * @param {number} bytes - Number of bytes to format
     * @returns {string} Precise formatted bytes string
     * 
     * @example
     * FormattedUtils.formatBytesPrecise(1024) // "1,00 KB"
     * FormattedUtils.formatBytesPrecise(1234567) // "1,18 MB"
     */
    static formatBytesPrecise(bytes) {
        return this.formatBytes(bytes, 2);
    }
    
}
